import { useState } from "react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  MoreVertical, User, Pencil, Car, Navigation, MapPin, MessageSquare, Bell, CheckCircle2,
  FileText, Wallet, DollarSign, AlertTriangle, Siren, Lock, Ban, Snowflake, KeyRound,
  Smartphone, StickyNote, Download, UserCog, Trash2, FlaskConical,
} from "lucide-react";
import { adminApi, formatApiError } from "./api";
import { toast } from "sonner";

export default function UserActionsMenu({ user, onChanged, onView, onNote, onMessage, onKyc, onTrips, onWallet }) {
  const [open, setOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  const toggleTest = async () => {
    const next = !user.is_test;
    try {
      const { data } = await adminApi.post(`/users/${user.id}/test-account`, { is_test: next });
      toast.success(data.message);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
    }
  };

  const patchStatus = async (status, label) => {
    try {
      await adminApi.patch(`/users/${user.id}`, { status });
      toast.success(`${user.name} — ${label}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
    }
  };

  const del = async () => {
    if (!reason.trim()) { toast.error("A reason is required to delete an account"); return; }
    setDelBusy(true);
    try {
      await adminApi.delete(`/users/${user.id}`, { data: { reason: reason.trim() } });
      toast.success(`${user.name} permanently deleted`);
      setDelOpen(false);
      setReason("");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally { setDelBusy(false); }
  };

  const action = async (a) => {
    try {
      const { data } = await adminApi.post(`/users/${user.id}/action`, { action: a });
      toast.success(data.message);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
    }
  };

  const toggleFreeze = async () => {
    try {
      const { data } = await adminApi.post(`/users/${user.id}/${user.is_frozen ? "unfreeze" : "freeze"}`);
      toast.success(data.frozen ? `${user.name} frozen — they cannot log in or book` : `${user.name} unfrozen`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
    }
  };

  const Item = ({ icon: Icon, label, onClick, danger }) => (
    <DropdownMenuItem
      data-testid={`action-${label.toLowerCase().replace(/[^a-z]+/g, "-")}-${user.id}`}
      onClick={onClick}
      className={`gap-2.5 ${danger ? "text-[#e4002b] focus:text-[#e4002b]" : "text-[#3a3f57]"}`}
    >
      <Icon className="h-4 w-4" strokeWidth={1.75} /> {label}
    </DropdownMenuItem>
  );

  return (
    <>
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button data-testid={`user-actions-${user.id}`} className="grid h-8 w-8 place-items-center rounded-lg text-[#565b73] transition-colors hover:bg-[#f4f6fb]">
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={4}
        collisionPadding={8}
        className="w-56 overflow-y-auto border border-[#12142b]/10 bg-white shadow-xl"
        style={{ maxHeight: "var(--radix-dropdown-menu-content-available-height)" }}
      >
        <Item icon={User} label="View Profile" onClick={() => onView?.(user)} />
        <Item icon={Pencil} label="Edit User" onClick={() => onView?.(user)} />
        <DropdownMenuSeparator />
        <Item icon={Car} label="View Trips" onClick={() => onTrips?.(user)} />
        <Item icon={Navigation} label="Active Ride" onClick={() => action("active-ride")} />
        <Item icon={MapPin} label="Live Location" onClick={() => action("live-location")} />
        <DropdownMenuSeparator />
        <Item icon={MessageSquare} label="Send Message" onClick={() => onMessage?.(user)} />
        <Item icon={Bell} label="Send Notification" onClick={() => action("send-notification")} />
        <DropdownMenuSeparator />
        <Item icon={CheckCircle2} label="Verify Documents" onClick={() => onKyc?.(user)} />
        <Item icon={FileText} label="View Documents" onClick={() => onKyc?.(user)} />
        <DropdownMenuSeparator />
        <Item icon={Wallet} label="Wallet & Payments" onClick={() => onWallet?.(user)} />
        <Item icon={DollarSign} label="Transactions" onClick={() => action("transactions")} />
        <DropdownMenuSeparator />
        <Item icon={AlertTriangle} label="View Reports" onClick={() => action("view-reports")} />
        <Item icon={Siren} label="Safety Incidents" onClick={() => action("safety-incidents")} />
        <DropdownMenuSeparator />
        <Item icon={Lock} label="Suspend Account" onClick={() => patchStatus("suspended", "suspended")} />
        <Item icon={Ban} label="Ban Account" onClick={() => patchStatus("banned", "banned")} danger />
        <Item icon={Snowflake} label={user.is_frozen ? "Unfreeze Account" : "Freeze Account"} onClick={toggleFreeze} />
        {user.status !== "active" && (
          <Item icon={CheckCircle2} label="Reactivate Account" onClick={() => patchStatus("active", "reactivated")} />
        )}
        <DropdownMenuSeparator />
        <Item icon={KeyRound} label="Reset Password" onClick={() => action("reset-password")} />
        <Item icon={Smartphone} label="Login History" onClick={() => action("login-history")} />
        <DropdownMenuSeparator />
        <Item icon={StickyNote} label="Add Admin Note" onClick={() => onNote?.(user)} />
        <Item icon={Download} label="Export User Data" onClick={() => action("export-data")} />
        <Item icon={UserCog} label="Login As User" onClick={() => action("login-as")} />
        <DropdownMenuSeparator />
        <Item icon={FlaskConical} label={user.is_test ? "Unmark Test Account" : "Mark As Test Account"} onClick={toggleTest} />
        <Item icon={Trash2} label="Delete Account" onClick={() => { setOpen(false); setDelOpen(true); }} danger />
      </DropdownMenuContent>
    </DropdownMenu>
    {delOpen && (
      <div className="fixed inset-0 z-50 grid place-items-center bg-[#12142b]/45 p-4" data-testid={`delete-account-dialog-${user.id}`}
        onClick={() => !delBusy && setDelOpen(false)}>
        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-[#12142b]">
            <Trash2 className="h-5 w-5 text-[#e4002b]" /> Delete account permanently?
          </h3>
          <p className="mt-2 text-sm text-[#565b73]">
            This permanently deletes <span className="font-semibold text-[#12142b]">{user.name}</span> and all of their data.
            This cannot be undone.
          </p>
          <label className="mt-4 block text-xs font-bold uppercase tracking-wide text-[#565b73]">Reason (required)</label>
          <textarea
            data-testid="delete-account-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Why is this account being deleted?"
            className="mt-1.5 w-full rounded-xl border border-[#12142b]/12 bg-[#f9fafd] px-3 py-2.5 text-sm text-[#12142b] outline-none focus:border-[#e4002b]/50"
          />
          <div className="mt-4 flex justify-end gap-2">
            <button data-testid="delete-account-cancel" onClick={() => setDelOpen(false)} disabled={delBusy}
              className="rounded-xl border border-[#12142b]/12 px-4 py-2.5 text-sm font-semibold text-[#565b73] hover:bg-[#f4f6fb]">Cancel</button>
            <button data-testid="delete-account-confirm" onClick={del} disabled={delBusy || !reason.trim()}
              className="rounded-xl bg-[#e4002b] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#b80022] disabled:opacity-50">
              {delBusy ? "Deleting…" : "Delete permanently"}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
